import { useApi } from 'api/api-context';
import { AuthListFaultSeveritiesResponse } from 'api/actions/auth-list-fault-severities/auth-list-fault-severities-response';
import {
  DataSelectInheritedProps,
  DataSelect,
  DataMultiSelectInheritedProps,
  DataMultiSelect,
} from 'components/selects/DataSelect';
import { useMemo } from 'react';

const LABEL_PROP = 'faultSeverityName' as const;
const VALUE_PROP = 'faultSeverityId' as const;
const ACTION_NAME = 'AuthListFaultSeverities' as const;
export const DISCONTINUED_FAULT_SEVERITY_IDS = [3] as const;

type FaultSeverityRow = AuthListFaultSeveritiesResponse[number];

interface FaultSeveritySelectBehaviorProps {
  excludedFaultSeverityIds?: number[];
  keepExcludedValueVisible?: boolean;
}

type FaultSeveritySelectProps = DataSelectInheritedProps & FaultSeveritySelectBehaviorProps;
type FaultSeverityMultiSelectProps = DataMultiSelectInheritedProps & FaultSeveritySelectBehaviorProps;

/**
 * Normalizes select values to strings for comparison.
 */
function normalizeSelectedValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined && item !== '').map((item) => String(item));
  }

  if (value === null || value === undefined || value === '') {
    return [];
  }

  return [String(value)];
}

/**
 * Select input for fault severities.
 */
export function FaultSeveritySelect({
  excludedFaultSeverityIds = [],
  keepExcludedValueVisible = false,
  value,
  defaultValue,
  ...props
}: FaultSeveritySelectProps) {
  const { getAction } = useApi();
  const selectedValues = useMemo(() => normalizeSelectedValues(value ?? defaultValue), [value, defaultValue]);
  const excludedIds = useMemo(
    () => new Set(excludedFaultSeverityIds.map((faultSeverityId) => String(faultSeverityId))),
    [excludedFaultSeverityIds]
  );
  const filterData = useMemo(
    () => (data: FaultSeverityRow[]) =>
      data.filter((severity) => {
        const severityId = String(severity.faultSeverityId);

        if (!excludedIds.has(severityId)) {
          return true;
        }

        return keepExcludedValueVisible && selectedValues.includes(severityId);
      }),
    [excludedIds, keepExcludedValueVisible, selectedValues]
  );

  return (
    <DataSelect
      action={getAction(ACTION_NAME)}
      labelProp={LABEL_PROP}
      valueProp={VALUE_PROP}
      value={value}
      defaultValue={defaultValue}
      filterData={filterData}
      {...props}
    />
  );
}

/**
 * Select input for fault severities (multiple).
 */
export function FaultSeverityMultiSelect({
  excludedFaultSeverityIds = [],
  keepExcludedValueVisible = false,
  value,
  defaultValue,
  ...props
}: FaultSeverityMultiSelectProps) {
  const { getAction } = useApi();
  const selectedValues = useMemo(() => normalizeSelectedValues(value ?? defaultValue), [value, defaultValue]);
  const excludedIds = useMemo(
    () => new Set(excludedFaultSeverityIds.map((faultSeverityId) => String(faultSeverityId))),
    [excludedFaultSeverityIds]
  );
  const filterData = useMemo(
    () => (data: FaultSeverityRow[]) =>
      data.filter((severity) => {
        const severityId = String(severity.faultSeverityId);

        if (!excludedIds.has(severityId)) {
          return true;
        }

        return keepExcludedValueVisible && selectedValues.includes(severityId);
      }),
    [excludedIds, keepExcludedValueVisible, selectedValues]
  );

  return (
    <DataMultiSelect
      action={getAction(ACTION_NAME)}
      labelProp={LABEL_PROP}
      valueProp={VALUE_PROP}
      value={value}
      defaultValue={defaultValue}
      filterData={filterData}
      {...props}
    />
  );
}
